import React, { forwardRef, InputHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { AlertCircle } from 'lucide-react';

interface BaseFormFieldProps {
  id?: string;
  name?: string;
  label?: string | React.ReactNode;
  required?: boolean;
  error?: string;
  helperText?: string;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  showCharCount?: boolean;
  maxLength?: number;
  containerClassName?: string;
  isTextarea?: boolean;
  rows?: number;
}

export type FormFieldProps = BaseFormFieldProps &
  InputHTMLAttributes<HTMLInputElement> &
  TextareaHTMLAttributes<HTMLTextAreaElement>;

export const FormField = forwardRef<HTMLInputElement & HTMLTextAreaElement, FormFieldProps>(
  (
    {
      id,
      name,
      label,
      required,
      error,
      helperText,
      prefix,
      suffix,
      showCharCount,
      maxLength,
      containerClassName = '',
      isTextarea = false,
      rows = 4,
      disabled,
      value,
      className = '',
      ...restProps
    },
    ref
  ) => {
    const inputId = id || (name ? `field_${name}` : undefined);
    const currentLength = typeof value === 'string' ? value.length : 0;

    return (
      <div className={`mb-3 relative ${containerClassName}`}>
        {label && (
          <div className="flex justify-between items-center mb-1">
            <label htmlFor={inputId} className="text-xs sm:text-sm font-bold text-slate-700 tracking-wide m-0">
              {label}
              {required && <span className="text-rose-500 ms-1">*</span>}
            </label>
            {showCharCount && maxLength && (
              <span className={`text-[11px] font-medium ${currentLength >= maxLength ? 'text-amber-600 font-bold' : 'text-slate-400'}`}>
                {currentLength}/{maxLength}
              </span>
            )}
          </div>
        )}

        <div className="relative">
          {prefix && (
            <div
              className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400 font-bold text-xs"
              style={{ zIndex: 4 }}
            >
              {prefix}
            </div>
          )}

          {isTextarea ? (
            <textarea
              id={inputId}
              name={name}
              ref={ref as any}
              rows={rows}
              disabled={disabled}
              maxLength={maxLength}
              value={value}
              className={`w-full rounded-xl border ${
                error
                  ? 'border-rose-300 bg-rose-50/20 text-rose-900 focus:ring-rose-500/20 focus:border-rose-500'
                  : 'border-slate-200 bg-slate-50/50 hover:bg-white focus:bg-white text-slate-800 focus:ring-[#1AA7DE]/20 focus:border-[#1AA7DE]'
              } px-3 py-2 text-xs sm:text-sm font-medium transition-all focus:outline-none focus:ring-2 disabled:bg-slate-100 disabled:cursor-not-allowed ${
                prefix ? 'pl-8' : ''
              } ${suffix ? 'pr-8' : ''} ${className}`}
              aria-invalid={!!error}
              {...(restProps as any)}
            />
          ) : (
            <input
              id={inputId}
              name={name}
              ref={ref as any}
              disabled={disabled}
              maxLength={maxLength}
              value={value}
              className={`w-full rounded-xl border ${
                error
                  ? 'border-rose-300 bg-rose-50/20 text-rose-900 focus:ring-rose-500/20 focus:border-rose-500'
                  : 'border-slate-200 bg-slate-50/50 hover:bg-white focus:bg-white text-slate-800 focus:ring-[#1AA7DE]/20 focus:border-[#1AA7DE]'
              } px-3 py-2 text-xs sm:text-sm font-medium transition-all focus:outline-none focus:ring-2 disabled:bg-slate-100 disabled:cursor-not-allowed ${
                prefix ? 'pl-8' : ''
              } ${suffix ? 'pr-8' : ''} ${className}`}
              aria-invalid={!!error}
              {...(restProps as any)}
            />
          )}

          {suffix && (
            <div
              className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-slate-400 font-bold text-xs"
              style={{ zIndex: 4 }}
            >
              {suffix}
            </div>
          )}
        </div>

        {error ? (
          <div className="flex items-center gap-1.5 mt-1 text-xs font-semibold text-rose-600" role="alert">
            <AlertCircle className="w-3.5 h-3.5 shrink-0" />
            <span>{error}</span>
          </div>
        ) : helperText ? (
          <p className="text-xs text-slate-400 font-normal mt-1 mb-0">{helperText}</p>
        ) : null}
      </div>
    );
  }
);

FormField.displayName = 'FormField';

export default FormField;
